/**
 * Aba "Juros & Inflação" da Mesa de Operações (#220) — visualização das
 * curvas do #218 (frontend only; consome /api/curva-pre e /api/ipca).
 *
 * Layout (linguagem da Curva de Mercado): header + Exportar; gráfico da
 * estrutura a termo com chips de série e comparação as-of; tabela "Vértices
 * ANBIMA"; bloco IPCA (realizado + implícita por horizonte + Focus
 * cross-check). Empty state enquanto o 1º sync não roda (cron diário 07:40).
 *
 * Comparação as-of: snapshots diários começam em PRIMEIRO_SNAPSHOT
 * (2026-06-10) — o picker limita o range e 404 vira mensagem amigável, não
 * quebra. data_ref trafega como STRING YYYY-MM-DD ponta a ponta (input
 * type=date → query) — nunca Date (timezone deslocaria o dia).
 */
import { useState } from "react";
import { useCurvaPre } from "../../../hooks/useCurvaPre";
import { useIpca } from "../../../hooks/useIpca";
import { CurvaJurosChart, SERIES_JUROS, montarPontos, type SerieJuros } from "./CurvaJurosChart";
import { IpcaBlock } from "./IpcaBlock";
import { ExportDropdown, type ExportColumn } from "../../ui/ExportDropdown";
import { dateCell, num } from "../../../utils/xlsxHelpers";
import type { PontoJuros } from "./CurvaJurosChart";

/** Primeiro snapshot diário materializado em prod (#218, go-live 11/06/2026). */
export const PRIMEIRO_SNAPSHOT = "2026-06-10";

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function JurosInflacaoTab() {
  const principal = useCurvaPre();
  const ipca = useIpca();
  const [selecionadas, setSelecionadas] = useState<SerieJuros[]>(["pre", "impl"]);
  // "" = sem comparação. String YYYY-MM-DD direto do input — nunca Date.
  const [cmpRef, setCmpRef] = useState<string>("");
  const comparacao = useCurvaPre(cmpRef || undefined);
  // #228 item 2: implícita/cupom as-of da MESMA data (cache chaveado).
  const comparacaoIpca = useIpca(cmpRef || undefined);

  function toggleSerie(s: SerieJuros) {
    setSelecionadas((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  if (principal.isLoading) {
    return (
      <div className="text-white/40 text-sm py-12 text-center" data-testid="juros-loading">
        Carregando curvas de juros…
      </div>
    );
  }

  if (principal.notFound || principal.error || !principal.data) {
    return (
      <div
        className="rounded-2xl border border-white/10 bg-indra-azul-surface p-8 text-center"
        data-testid="juros-empty"
      >
        <h3 className="text-white font-semibold mb-2">Sem curva pré materializada</h3>
        <p className="text-white/60 text-sm mb-3">
          {principal.error
            ? "Erro ao carregar a curva. Tente recarregar a página."
            : "Aguardando o primeiro sync da curva — cron diário às 07:40 (BRT)."}
        </p>
        <p className="text-white/40 text-xs">
          Para popular manualmente, rode via run-task:
          <br />
          <code className="text-indra-turquesa">python scripts/curva_pre_ipca_sync.py</code>
        </p>
      </div>
    );
  }

  const curva = principal.data;
  const cmpAtiva = cmpRef !== "" && cmpRef !== curva.data_ref;
  const cmpData = cmpAtiva && !comparacao.isLoading ? comparacao.data : null;
  const cmpNotFound = cmpAtiva && comparacao.notFound;
  const cmpIpcaData =
    cmpAtiva && !comparacaoIpca.isLoading ? comparacaoIpca.data : null;

  // Export: grade visível (séries selecionadas + comparação quando ativa).
  const pontos = montarPontos(curva, ipca.data, cmpData, cmpIpcaData);
  const exportCols: ExportColumn<PontoJuros & Record<string, unknown>>[] = [
    { key: "venc", header: "Vencimento", format: (r) => dateCell(r.venc) },
    { key: "du", header: "DU", format: (r) => (r.du !== undefined ? num(r.du, 0) : null) },
  ];
  for (const s of SERIES_JUROS) {
    if (!selecionadas.includes(s.key)) continue;
    exportCols.push({
      key: s.key,
      header: `${s.label} % a.a. (${curva.data_ref})`,
      format: (r) => {
        const v = (r[s.key] ?? r[`${s.key}X` as keyof PontoJuros]) as number | undefined;
        return v !== undefined ? num(v, 4) : null;
      },
    });
    exportCols.push({
      key: `${s.key}_extrapolado`,
      header: `${s.label} extrapolado`,
      format: (r) => (r[`${s.key}Extrapolado` as keyof PontoJuros] ? "sim" : ""),
    });
  }
  if (cmpData) {
    exportCols.push({
      key: "preCmp",
      header: `Pré % a.a. (${cmpData.data_ref})`,
      format: (r) => (r.preCmp !== undefined ? num(r.preCmp, 4) : null),
    });
  }
  // #228 item 2: comparação da implícita/cupom também sai no export.
  if (cmpIpcaData) {
    const ref = cmpIpcaData.data_ref_implicita ?? cmpRef;
    if (selecionadas.includes("impl")) {
      exportCols.push({
        key: "implCmp",
        header: `Implícita % a.a. (${ref})`,
        format: (r) => (r.implCmp !== undefined ? num(r.implCmp, 4) : null),
      });
    }
    if (selecionadas.includes("cupom")) {
      exportCols.push({
        key: "cupomCmp",
        header: `Cupom IPCA % a.a. (${ref})`,
        format: (r) => (r.cupomCmp !== undefined ? num(r.cupomCmp, 4) : null),
      });
    }
  }

  return (
    <div data-testid="juros-inflacao-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-white font-semibold">Juros &amp; Inflação — ANBIMA/BCB</h2>
          <p className="text-white/50 text-xs mt-1">
            Estrutura a termo % a.a. (DU/252) — pré, inflação implícita e cupom
            IPCA · as-of {fmtDate(curva.data_ref)} · {curva.fonte}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <label className="flex items-center gap-2 text-white/50">
            comparar com
            <input
              type="date"
              value={cmpRef}
              min={PRIMEIRO_SNAPSHOT}
              max={curva.data_ref}
              onChange={(e) => setCmpRef(e.target.value)}
              data-testid="juros-cmp-picker"
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/80 text-xs [color-scheme:dark]"
            />
            {cmpRef && (
              <button
                type="button"
                onClick={() => setCmpRef("")}
                className="text-white/40 hover:text-white/80"
                data-testid="juros-cmp-limpar"
                title="Limpar comparação"
              >
                ✕
              </button>
            )}
          </label>
          <ExportDropdown<PontoJuros & Record<string, unknown>>
            filename={`cortex-juros-inflacao-${curva.data_ref.replace(/-/g, "")}`}
            primarySheetName="Juros & Inflação"
            columns={exportCols}
            rows={pontos as Array<PontoJuros & Record<string, unknown>>}
          />
        </div>
      </div>

      {/* Range disponível / 404 da comparação — sem quebrar a tela. */}
      {cmpRef === curva.data_ref && (
        <p className="text-white/40 text-xs mb-2" data-testid="juros-cmp-mesma-data">
          A data escolhida é a própria curva exibida — escolha uma data anterior.
        </p>
      )}
      {cmpNotFound && (
        <p className="text-amber-300/80 text-xs mb-2" data-testid="juros-cmp-404">
          Sem curva em {fmtDate(cmpRef)} — snapshots diários começam em{" "}
          {fmtDate(PRIMEIRO_SNAPSHOT)} (dias úteis).
        </p>
      )}
      {curva.data_ref === PRIMEIRO_SNAPSHOT && !cmpRef && (
        <p className="text-white/35 text-xs mb-2" data-testid="juros-cmp-hint-range">
          Histórico as-of: por enquanto só {fmtDate(PRIMEIRO_SNAPSHOT)} está
          materializado — a comparação ganha sentido conforme os snapshots
          diários acumulam.
        </p>
      )}

      <CurvaJurosChart
        principal={curva}
        ipca={ipca.data}
        comparacao={cmpData}
        comparacaoIpca={cmpIpcaData}
        selecionadas={selecionadas}
        onToggle={toggleSerie}
      />

      {/* Vértices ANBIMA — compacta, padrão tabela da Curva de Mercado.
          #228: 3 linhas (Pré / Cupom IPCA / Implícita) nos dus da PRÉ, 2
          decimais — Fisher conferível no olho. A cauda do cupom (NTN-B vai
          além da pré) fica fora: colunas = vértices pré. */}
      {curva.vertices_originais.length > 0 && (() => {
        const fmt2 = (n: number) =>
          n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const porDu = (rows: { du: number; taxa_aa: number }[]) =>
          new Map(rows.map((v) => [v.du, v.taxa_aa]));
        const cupomPorDu = porDu(curva.vertices_ipca ?? []);
        const implPorDu = porDu(curva.vertices_implicita ?? []);
        const linhas: Array<{ rotulo: string; cor: string; valores: Map<number, number> }> = [
          { rotulo: "Pré", cor: "text-white/80", valores: porDu(curva.vertices_originais) },
          { rotulo: "Cupom IPCA", cor: "text-indigo-300/80", valores: cupomPorDu },
          { rotulo: "Implícita", cor: "text-amber-300/80", valores: implPorDu },
        ].filter((l) => l.valores.size > 0);
        return (
          <div className="mb-4" data-testid="juros-vertices-section">
            <h3 className="text-white font-semibold text-sm mb-1">Vértices ANBIMA (% a.a.)</h3>
            <p className="text-white/50 text-xs mb-2">
              Vértices publicados das ETTJs — a grade mensal acima é o Svensson
              avaliado no du de cada vencimento. Implícita ≈ (1+Pré)/(1+Cupom)−1.
            </p>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-indra-azul-surface text-white/60 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">du</th>
                    {curva.vertices_originais.map((v) => (
                      <th key={v.du} className="px-3 py-2 text-right font-mono">{v.du}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr
                      key={l.rotulo}
                      className="border-t border-white/5"
                      data-testid={`juros-vertices-row-${l.rotulo === "Pré" ? "pre" : l.rotulo === "Implícita" ? "impl" : "cupom"}`}
                    >
                      <td className="px-3 py-2 text-white/60 whitespace-nowrap">{l.rotulo}</td>
                      {curva.vertices_originais.map((v) => {
                        const val = l.valores.get(v.du);
                        return (
                          <td key={v.du} className={`px-3 py-2 text-right font-mono ${l.cor}`}>
                            {val !== undefined ? fmt2(val) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {ipca.data && <IpcaBlock ipca={ipca.data} />}
      {!ipca.data && !ipca.isLoading && (
        <div className="text-white/40 text-xs py-4 text-center" data-testid="juros-ipca-indisponivel">
          Bloco IPCA indisponível (erro ao carregar /api/ipca).
        </div>
      )}
    </div>
  );
}
